import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { PaymentMethod, SchoolInfo } from "@/types";
import type { CashierDay, CashOperation } from "@/services/closingService";
import { PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import { drawImageContain, loadPdfImage, pdfDate, pdfMoney } from "./pdfImages";

const GREEN: [number, number, number] = [29, 95, 208];
const RED: [number, number, number] = [176, 56, 40];
const TEXT: [number, number, number] = [40, 40, 38];
const MUTED: [number, number, number] = [115, 115, 110];
const METHODS = Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[];

const time = (iso: string) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

/**
 * Fiche de caisse d'un caissier pour un jour : attendu et compté par mode, écart, détail des opérations et
 * signatures. Tant que la caisse n'est pas validée, la fiche est un simple état provisoire.
 */
export async function exportCashClosingPdf(
  cashier: CashierDay,
  operations: CashOperation[],
  date: string,
  school: SchoolInfo,
): Promise<void> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = 210;
  const margin = 16;
  const logo = await loadPdfImage(school.logo);
  const closing = cashier.closing;

  let x = margin;
  if (logo) {
    drawImageContain(doc, logo, margin, 12, 16, 16);
    x = margin + 20;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...GREEN);
  doc.text(school.name.toUpperCase(), x, 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text([school.city, school.phone].filter(Boolean).join(" · "), x, 23);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...TEXT);
  doc.text(closing ? "CLÔTURE DE CAISSE" : "ÉTAT DE CAISSE (non validé)", pageW - margin, 18, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Journée du ${pdfDate(date)}`, pageW - margin, 24, { align: "right" });

  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.6);
  doc.line(margin, 31, pageW - margin, 31);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Caissier :", margin, 39);
  doc.setFont("helvetica", "normal");
  doc.text(cashier.cashierName, margin + 20, 39);
  doc.text(
    `${cashier.operations} opération(s) encaissée(s)${cashier.cancelled ? `, ${cashier.cancelled} annulée(s)` : ""}`,
    pageW - margin,
    39,
    { align: "right" },
  );

  // Rapprochement par mode.
  const rows = METHODS.filter((m) => (cashier.expected[m] ?? 0) > 0 || (closing?.counted[m] ?? 0) > 0).map((m) => {
    const expected = cashier.expected[m] ?? 0;
    const counted = closing ? (closing.counted[m] ?? 0) : null;
    return [
      PAYMENT_METHOD_LABEL[m],
      pdfMoney(expected),
      counted === null ? "—" : pdfMoney(counted),
      counted === null ? "—" : pdfMoney(counted - expected),
    ];
  });
  const countedTotal = closing ? METHODS.reduce((acc, m) => acc + (closing.counted[m] ?? 0), 0) : null;
  autoTable(doc, {
    startY: 45,
    margin: { left: margin, right: margin },
    head: [["Mode", "Attendu (système)", "Compté", "Écart"]],
    body: rows,
    foot: [["Total", pdfMoney(cashier.total), countedTotal === null ? "—" : pdfMoney(countedTotal), closing ? pdfMoney(closing.gap) : "—"]],
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9, cellPadding: 2.2, textColor: TEXT, lineColor: [206, 206, 200], lineWidth: 0.2 },
    headStyles: { fillColor: [234, 238, 245], textColor: [55, 60, 55], fontStyle: "bold" },
    footStyles: {
      fillColor: closing && closing.gap !== 0 ? [250, 232, 228] : [226, 236, 252],
      textColor: closing && closing.gap !== 0 ? RED : GREEN,
      fontStyle: "bold",
    },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
  });
  let y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 80;

  if (closing) {
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(...TEXT);
    doc.text(
      `Comptée et validée le ${pdfDate(closing.createdAt)} à ${time(closing.createdAt)} par ${closing.validatedByName}.`,
      margin,
      y,
    );
    if (closing.comment) {
      y += 5;
      doc.setFont("helvetica", "italic");
      doc.text(doc.splitTextToSize(`Explication de l'écart : ${closing.comment}`, pageW - margin * 2), margin, y);
      doc.setFont("helvetica", "normal");
      y += 5;
    }
  }

  // Détail des opérations.
  autoTable(doc, {
    startY: y + 6,
    margin: { left: margin, right: margin },
    head: [["Heure", "Référence", "Libellé", "Mode", "Montant"]],
    body: operations.map((o) => [
      time(o.createdAt),
      o.reference,
      o.cancelled ? `${o.label} (ANNULÉ)` : o.label,
      PAYMENT_METHOD_LABEL[o.method],
      o.cancelled ? "—" : pdfMoney(o.amount),
    ]),
    theme: "grid",
    styles: { font: "helvetica", fontSize: 8, cellPadding: 1.8, textColor: TEXT, lineColor: [206, 206, 200], lineWidth: 0.2 },
    headStyles: { fillColor: [234, 238, 245], textColor: [55, 60, 55], fontStyle: "bold" },
    columnStyles: { 0: { cellWidth: 14 }, 1: { cellWidth: 34 }, 3: { cellWidth: 28 }, 4: { cellWidth: 28, halign: "right" } },
  });
  y = ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 22;
  if (y > 270) {
    doc.addPage();
    y = 30;
  }

  doc.setDrawColor(150, 150, 145);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + 60, y);
  doc.line(pageW - margin - 60, y, pageW - margin, y);
  doc.setFontSize(9);
  doc.setTextColor(...TEXT);
  doc.text("Le caissier", margin, y + 5);
  doc.text("Le contrôleur", pageW - margin - 60, y + 5);

  doc.save(`caisse-${date}-${cashier.cashierName.replace(/[^\p{L}\p{N}]+/gu, "-")}.pdf`);
}
